import React from 'react';
import ReactDOM from 'react-dom';

interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  type?: 'danger' | 'warning' | 'info';
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  type = 'danger',
}) => {
  if (!isOpen) return null;

  const dialogContent = (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-md w-full shadow-2xl animate-fadeIn">
        {/* Header */}
        <div className="px-6 py-4" style={{ backgroundColor: '#ebebeb', borderBottom: '1px solid var(--color-border)' }}>
          <div className="flex items-center gap-3">
            <div
              className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center shadow-sm"
              style={{ backgroundColor: '#4A5568' }}
            >
              {type === 'danger' && (
                <svg className="w-6 h-6" fill="none" stroke="white" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-1.924-1.333-2.732 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              )}
              {type === 'warning' && (
                <svg className="w-6 h-6" fill="none" stroke="white" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-1.924-1.333-2.732 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              )}
              {type === 'info' && (
                <svg className="w-6 h-6" fill="none" stroke="white" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
            </div>
            <h3
              className="text-lg font-semibold"
              style={{ fontFamily: 'var(--font-heading)', color: 'var(--color-primary)' }}
            >
              {title}
            </h3>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-5">
          <p
            className="leading-relaxed"
            style={{ fontFamily: 'var(--font-body)', color: 'var(--color-primary)' }}
          >
            {message}
          </p>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 flex justify-end gap-3" style={{ backgroundColor: '#fafafa', borderTop: '1px solid var(--color-border)' }}>
          <button
            onClick={onCancel}
            className="px-5 py-2.5 rounded-lg transition-all font-medium text-sm"
            style={{
              fontFamily: 'var(--font-body)',
              color: 'var(--color-primary)',
              backgroundColor: 'white',
              border: '1px solid var(--color-border)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f5f5f5';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'white';
            }}
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            className="px-5 py-2.5 rounded-lg transition-all font-semibold text-sm"
            style={{
              fontFamily: 'var(--font-body)',
              backgroundColor: 'white',
              color: type === 'danger' ? '#dc2626' : type === 'warning' ? '#d97706' : '#2563eb',
              border: `1px solid ${type === 'danger' ? '#dc2626' : type === 'warning' ? '#d97706' : '#2563eb'}`,
            }}
            onMouseEnter={(e) => {
              if (type === 'danger') {
                e.currentTarget.style.backgroundColor = '#fef2f2';
                e.currentTarget.style.borderColor = '#b91c1c';
                e.currentTarget.style.color = '#b91c1c';
              } else if (type === 'warning') {
                e.currentTarget.style.backgroundColor = '#fffbeb';
                e.currentTarget.style.borderColor = '#b45309';
                e.currentTarget.style.color = '#b45309';
              } else {
                e.currentTarget.style.backgroundColor = '#eff6ff';
                e.currentTarget.style.borderColor = '#1d4ed8';
                e.currentTarget.style.color = '#1d4ed8';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'white';
              if (type === 'danger') {
                e.currentTarget.style.borderColor = '#dc2626';
                e.currentTarget.style.color = '#dc2626';
              } else if (type === 'warning') {
                e.currentTarget.style.borderColor = '#d97706';
                e.currentTarget.style.color = '#d97706';
              } else {
                e.currentTarget.style.borderColor = '#2563eb';
                e.currentTarget.style.color = '#2563eb';
              }
            }}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );

  // Use React Portal to render at document.body level
  return ReactDOM.createPortal(dialogContent, document.body);
};

export default ConfirmDialog;
